import { useMemo } from 'react'
import { generateHTML } from '@tiptap/html'
import StarterKit from '@tiptap/starter-kit'
import { FlightItinerary } from './extensions/FlightItinerary'
import { PageBreak } from './extensions/PageBreak'

/**
 * PageView Component
 *
 * Renders a single page with static HTML content (no editor instance).
 * Used in paged mode preview and print preview.
 *
 * @param {Object} content - TipTap JSON content for this page
 * @param {number} pageNumber - Current page number (1-indexed)
 * @param {number} totalPages - Total number of pages
 * @param {string|function|null} header - Header content or render function
 * @param {string|function|null} footer - Footer content or render function
 * @param {number} headerHeight - Header height in pixels
 * @param {number} footerHeight - Footer height in pixels
 * @param {string} pageFormat - 'A4' or 'Letter'
 * @param {function} onDoubleClick - Optional callback when page is double-clicked
 */
export default function PageView({
  content,
  pageNumber,
  totalPages,
  header = null,
  footer = null,
  headerHeight = 60,
  footerHeight = 60,
  pageFormat = 'A4',
  onDoubleClick = null,
  showPageNumbers = true,
}) {
  // Page dimensions in pixels (at 96 DPI)
  const pageFormats = {
    A4: {
      width: 794, // 210mm
      height: 1123, // 297mm
    },
    Letter: {
      width: 816, // 8.5 inches
      height: 1056, // 11 inches
    },
  }

  const format = pageFormats[pageFormat] || pageFormats.A4

  // Extensions used to render content
  const extensions = [StarterKit, FlightItinerary, PageBreak]

  // Generate static HTML from TipTap JSON
  // This is MUCH faster than creating editor instances
  const html = useMemo(() => {
    try {
      return generateHTML(content, extensions)
    } catch (error) {
      console.error('Error generating HTML from TipTap content:', error)
      return '<p>Error rendering page content</p>'
    }
  }, [content])

  // Render header with page number support
  const renderHeader = () => {
    if (!header) return null

    if (typeof header === 'function') {
      return header(pageNumber, totalPages)
    }

    // Replace tokens
    const headerText = String(header)
      .replace('{page}', pageNumber)
      .replace('{total}', totalPages)

    return <span className="text-sm">{headerText}</span>
  }

  // Render footer with page number support
  const renderFooter = () => {
    if (footer === null && !showPageNumbers) return null

    if (typeof footer === 'function') {
      return footer(pageNumber, totalPages)
    }

    // Default footer with page numbers
    if (footer === null && showPageNumbers) {
      return (
        <span className="text-sm text-slate-500">
          Page {pageNumber} of {totalPages}
        </span>
      )
    }

    // Replace tokens
    const footerText = String(footer)
      .replace('{page}', pageNumber)
      .replace('{total}', totalPages)

    return <span className="text-sm">{footerText}</span>
  }

  return (
    <div
      className="paged-editor-page"
      style={{
        width: `${format.width}px`,
        height: `${format.height}px`, // Fixed height, not minHeight
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
      }}
      onDoubleClick={onDoubleClick}
    >
      {/* Header */}
      {(header || showPageNumbers) && (
        <div
          className="paged-editor-header"
          style={{ height: `${headerHeight}px`, flexShrink: 0 }}
        >
          <div className="flex items-center justify-between h-full">
            {renderHeader()}
          </div>
        </div>
      )}

      {/* Content Area */}
      <div
        className="paged-editor-page-content"
        style={{
          flex: 1,
          overflow: 'hidden', // Don't allow overflow
          position: 'relative',
        }}
      >
        {/* Render static HTML */}
        <div
          className="page-view-html-content"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>

      {/* Footer */}
      {(footer !== null || showPageNumbers) && (
        <div
          className="paged-editor-footer"
          style={{ height: `${footerHeight}px`, flexShrink: 0 }}
        >
          <div className="flex items-center justify-center h-full">
            {renderFooter()}
          </div>
        </div>
      )}
    </div>
  )
}
